import storeJsEngine from 'store/src/store-engine'
import localforage from 'localforage'
import expirePlugin from 'store/plugins/expire'
import updatePlugin from 'store/plugins/update'

// IndexedDB 인스턴스 생성
const idb = localforage.createInstance({
  name: 'LinkMeMine',
  version: 1.0,
  storeName: 'keyvaluepairs',
  description: 'LinkMeMine Web Manager Storage',
  driver: localforage.INDEXEDDB,  // IndexedDB 강제
})

// 메모리 캐시 (RAM에 데이터 저장하여 빠른 접근 제공)
class MemoryCache {
  constructor() {
    this._cache = new Map()
  }
  
  // 캐시에서 값 가져오기
  get(key) {
    return this._cache.get(key)
  }
  
  // 캐시에 값 저장하기
  set(key, value) {
    this._cache.set(key, value)
  }
  
  // 캐시에서 값 삭제하기
  delete(key) {
    return this._cache.delete(key)
  }
  
  // 캐시 전체 비우기
  clear() {
    this._cache.clear()
  }
  
  // 캐시 항목 순회하기
  forEach(fn) {
    this._cache.forEach(fn)
  }
  
  // 저장된 항목 개수
  get size() {
    return this._cache.size
  }
}

const cache = new MemoryCache()

// 초기화 (앱 시작 시 자동 실행)
const initPromise = (async () => {
  try {
    if (!window.indexedDB) {
      throw new Error('IndexedDB not supported in this browser')
    }
    
    await migrateFromLocalStorage()
    
    const keys = await idb.keys()
    
    await Promise.all(
      keys.map(async (key) => {
        const value = await idb.getItem(key)
        cache.set(key, value)
      }),
    )
    
    return true
  } catch (err) {
    console.error('[Store] 초기화 실패:', err)
    cache.clear()
    return false
  }
})()

// localStorage → IndexedDB 마이그레이션 (initPromise 메서드 내부에서 사용)
async function migrateFromLocalStorage() {
  try {
    const MIGRATION_KEY = '__lmm_migrated_v2__'

    // 이미 마이그레이션 했는지 확인
    if (localStorage.getItem(MIGRATION_KEY)) {
      return
    }

    const keys = Object.keys(localStorage)

    if (keys.length === 0) {
      localStorage.setItem(MIGRATION_KEY, new Date().toISOString())
      return
    }

    for (const key of keys) {
      try {
        const value = localStorage.getItem(key)
        // JSON 파싱 시도, 실패하면 원본 사용
        const parsedValue = (() => {
          try { return JSON.parse(value) }
          catch { return value }
        })()
        await idb.setItem(key, parsedValue)
      } catch (err) {
        console.warn(`[Store] "${key}" 마이그레이션 실패:`, err)
      }
    }

    localStorage.setItem(MIGRATION_KEY, new Date().toISOString())
  } catch (err) {
    // localStorage 접근 불가 시 마이그레이션 스킵 (private browsing 등)
    console.warn('[Store] localStorage 접근 불가 - 마이그레이션 스킵')
  }
}

// Custom Storage Driver (store.js 라이브러리 인터페이스 규격 준수)
const indexedDBStorageDriver = {
  // 캐시에서 데이터 읽기
  read(key) {
    return cache.get(key)
  },

  // 캐시에 즉시 저장 후, 백그라운드에서 IndexedDB에 저장
  write(key, value) {
    cache.set(key, value)
    idb.setItem(key, value).catch(err => {
      console.error(`[Store] "${key}"를 IndexedDB에 저장 실패:`, err)
    })
  },

  // 모든 항목 순회
  each(fn) {
    cache.forEach((value, key) => fn(value, key))
  },

  // 캐시와 IndexedDB에서 데이터 삭제
  remove(key) {
    cache.delete(key)
    idb.removeItem(key).catch(err => {
      console.error(`[Store] "${key}"를 IndexedDB에서 삭제 실패:`, err)
    })
  },

  // 캐시와 IndexedDB 전체 초기화
  clearAll() {
    cache.clear()
    idb.clear().catch(err => {
      console.error('[Store] IndexedDB 비우기 실패:', err)
    })
  },
}

/**
  Store 인스턴스 생성
  - storage: IndexedDB (localStorage 대신 대용량 지원)
  - plugins: expire, update
 */
const store = storeJsEngine.createStore([indexedDBStorageDriver])

store.addPlugin(expirePlugin)
store.addPlugin(updatePlugin)

// Storage 초기화 완료를 기다리는 메서드
store.waitForInitialization = () => initPromise

// 메모리 캐시를 IndexedDB에 즉시 동기화
store.flush = async () => {
  const promises = []
  cache.forEach((value, key) => {
    promises.push(idb.setItem(key, value))
  })
  await Promise.all(promises)
}

// 캐시와 IndexedDB 전체 삭제
store.reset = async () => {
  cache.clear()
  await idb.clear()
}

// Session Storage

/**
 * sessionStorage 래퍼
 *
 * 차이점:
 * - store: 브라우저를 닫아도 데이터 유지 (IndexedDB)
 * - session: 브라우저 탭을 닫으면 데이터 사라짐 (sessionStorage)
 */
const session = {
  get(key) {
    const value = sessionStorage.getItem(key)
    if (!value) {
      return null
    }
    try {
      return JSON.parse(value)
    } catch {
      return value
    }
  },
  
  set(key, value) {
    const serialized = typeof value === 'string'
      ? value
      : JSON.stringify(value)
    sessionStorage.setItem(key, serialized)
  },
  
  remove(key) {
    sessionStorage.removeItem(key)
  },
  
  clear() {
    sessionStorage.clear()
  },
}

export default store
export {session, store}
